import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode as QrCodeIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { Service, Branch } from '../types';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DAYS_MAP: { [key: number]: string } = {
  0: 'Dom',
  1: 'Lun',
  2: 'Mar',
  3: 'Mié',
  4: 'Jue',
  5: 'Vie',
  6: 'Sáb'
};

const Services: React.FC = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [branchFilter, setBranchFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [qrCodeImage, setQrCodeImage] = useState<string | null>(null);

  useEffect(() => {
    loadServices();
    loadBranches();
  }, []);

  const loadBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setBranches(await response.json());
      }
    } catch (err) {
      console.error('Error al cargar sucursales:', err);
    }
  };

  const loadServices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/services`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar servicios');
      }

      const data = await response.json();
      setServices(data);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar servicios');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedService) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/services/${selectedService.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al eliminar servicio');
      }

      await loadServices();
      setDeleteDialogOpen(false);
      setSelectedService(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al eliminar servicio');
    }
  };

  const handleShowQR = async (service: Service) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/services/${service.id}/qr`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al generar código QR');
      }

      const data = await response.json();
      setQrCodeImage(data.qrCodeImage);
      setSelectedService(service);
      setQrDialogOpen(true);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al generar código QR');
    }
  };

  const handleDownloadQR = () => {
    if (!qrCodeImage || !selectedService) return;

    const link = document.createElement('a');
    link.href = qrCodeImage;
    link.download = `QR-${selectedService.name}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getDaysWithTasks = (service: Service): string => {
    if (!service.tasks || service.tasks.length === 0) {
      return 'Sin tareas';
    }

    const daysSet = new Set(service.tasks.map(t => t.dayOfWeek));
    const days = Array.from(daysSet).sort((a, b) => a - b);
    
    return days.map(d => DAYS_MAP[d]).join(', ');
  };

  const filteredServices = Array.isArray(services) ? services.filter(service => {
    if (branchFilter && service.branchId !== branchFilter) return false;
    return true;
  }) : [];

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Nombre',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'client',
      headerName: 'Cliente',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<Service>) => {
        const service = params.row;
        if (service.company) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon fontSize="small" color="primary" />
              <Typography variant="body2">{service.company.name}</Typography>
            </Box>
          );
        }
        if (service.individual) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon fontSize="small" color="secondary" />
              <Typography variant="body2">{service.individual.name}</Typography>
            </Box>
          );
        }
        return '-';
      }
    },
    {
      field: 'responsibles',
      headerName: 'Responsables',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<Service>) => {
        const service = params.row;
        if (!service.responsibles || service.responsibles.length === 0) {
          return <Typography variant="body2" color="text.secondary">Sin responsables</Typography>;
        }
        
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {service.responsibles.slice(0, 2).map((resp) => (
              <Chip
                key={resp.id}
                label={`${resp.user.firstName} ${resp.user.lastName}`}
                size="small"
                variant="outlined"
              />
            ))}
            {service.responsibles.length > 2 && (
              <Chip
                label={`+${service.responsibles.length - 2}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        );
      }
    },
    {
      field: 'days',
      headerName: 'Días con tareas',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<Service>) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon fontSize="small" />
            <Typography variant="body2">{getDaysWithTasks(params.row)}</Typography>
          </Box>
        );
      }
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 120,
      renderCell: (params: GridRenderCellParams<Service>) => {
        return (
          <Chip
            label={params.row.isActive ? 'Activo' : 'Inactivo'}
            color={params.row.isActive ? 'success' : 'default'}
            size="small"
          />
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams<Service>) => {
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => navigate(`/services/${params.row.id}`)}
              title="Ver detalles"
            >
              <CalendarIcon />
            </IconButton>
            <IconButton
              size="small"
              color="info"
              onClick={() => handleShowQR(params.row)}
              title="Ver código QR"
            >
              <QrCodeIcon />
            </IconButton>
            <IconButton
              size="small"
              color="primary"
              onClick={() => navigate(`/services/${params.row.id}/edit`)}
              title="Editar"
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setSelectedService(params.row);
                setDeleteDialogOpen(true);
              }}
              title="Eliminar"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        );
      }
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Servicios
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/services/new')}
        >
          Nuevo Servicio
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Sucursal</InputLabel>
          <Select
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            label="Sucursal"
          >
            <MenuItem value="">Todas</MenuItem>
            {branches.map(b => (
              <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <DataGrid
          rows={filteredServices}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } }
          }}
          autoHeight
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none'
            }
          }}
        />
      </Paper>

      {/* Diálogo de eliminación */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar el servicio &quot;{selectedService?.name}&quot;?
            Esta acción no se puede deshacer y se eliminarán todos los comentarios y fotos asociados.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de código QR */}
      <Dialog 
        open={qrDialogOpen} 
        onClose={() => setQrDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Código QR - {selectedService?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {qrCodeImage && (
              <img 
                src={qrCodeImage} 
                alt="Código QR" 
                style={{ width: '100%', maxWidth: 400 }}
              />
            )}
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Este código QR permite ver las tareas del servicio sin necesidad de autenticación.
              Ideal para que cualquier persona pueda escanear y ver qué tareas hay que hacer.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>
            Cerrar
          </Button>
          <Button onClick={handleDownloadQR} variant="contained" startIcon={<QrCodeIcon />}>
            Descargar QR
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Services;

